// Deliberate module cycle with ./logger (pre-existing, moved as a unit):
// logger.ts uses originalConsoleMethods for its browser write handlers;
// interceptConsoleMethods routes console.* through the logger. Only function
// bodies touch the circular binding, so evaluation order is safe.
import { logger } from './logger';
import { type CustomLogger } from './types';

type ConsoleMethod = 'log' | 'debug' | 'info' | 'warn' | 'error';

export const originalConsoleMethods: Record<ConsoleMethod, typeof console.log> = {
  log: console.log,
  debug: console.debug,
  info: console.info,
  warn: console.warn,
  error: console.error,
};

/**
 * Tauri's IPC bridge warns through `console.warn` when a callback id is
 * delivered to after its callback is gone. The id is a random u32 that names
 * neither the event nor the command it belongs to, and the function that
 * emits the warning is installed non-writable, so the id cannot be enriched at
 * the source. This interceptor is where the string arrives, so this is where a
 * name can be attached — supplied by whoever minted the id.
 */
const TAURI_ORPHANED_CALLBACK_MARKER = "Couldn't find callback id";
const TAURI_ORPHANED_CALLBACK_ID = /Couldn't find callback id (\d+)/;

export type TauriCallbackLabel = { kind: string; name: string };
export type TauriCallbackLabelResolver = (
  callbackId: string
) => TauriCallbackLabel | undefined;

let resolveTauriCallbackLabel: TauriCallbackLabelResolver | undefined;

/**
 * App-side wiring (`src/composition/wiring/runtime-wiring.ts`). Without it the warning is
 * forwarded exactly as before — this is additive, and an unresolvable id costs
 * one failed Map lookup.
 */
export function setTauriCallbackLabelResolver(
  resolver: TauriCallbackLabelResolver | undefined
): void {
  resolveTauriCallbackLabel = resolver;
  labelledLoggers.clear();
}

// One child logger per distinct label, not per warning: the labelled path is
// the highest-volume warn line in the product and a child allocation per
// delivery would be paid tens of times a second. Bounded by the number of
// distinct event/command names, which is small.
//
// Routing through `child` rather than an extra argument is also what keeps
// this inert for the Datadog burst cap: `buildLogSignature` (./burst-throttle)
// signs the message and `additionalArgs` and deliberately excludes bindings,
// so labelling changes which ATTRIBUTES a line carries, never whether it is
// admitted.
const labelledLoggers = new Map<string, CustomLogger>();

function labelledLoggerFor(label: TauriCallbackLabel): CustomLogger {
  const key = `${label.kind}:${label.name}`;
  let child = labelledLoggers.get(key);
  if (child === undefined) {
    child = logger.child({
      orphaned_callback_kind: label.kind,
      orphaned_callback_name: label.name,
    });
    labelledLoggers.set(key, child);
  }
  return child;
}

function warnLogger(args: unknown[]): CustomLogger {
  if (resolveTauriCallbackLabel === undefined) return logger;
  const [message] = args;
  if (typeof message !== 'string') return logger;
  if (!message.includes(TAURI_ORPHANED_CALLBACK_MARKER)) return logger;

  const id = TAURI_ORPHANED_CALLBACK_ID.exec(message)?.[1];
  if (id === undefined) return logger;

  const label = resolveTauriCallbackLabel(id);
  return label === undefined ? logger : labelledLoggerFor(label);
}

export function interceptConsoleMethods(): void {
  console.log = (...args: unknown[]) => {
    logger.log(...args);
  };

  console.debug = (...args: unknown[]) => {
    logger.debug(...args);
  };

  console.info = (...args: unknown[]) => {
    logger.info(...args);
  };

  console.warn = (...args: unknown[]) => {
    warnLogger(args).warn(...args);
  };

  console.error = (...args: unknown[]) => {
    logger.error(...args);
  };
}
